import { useId, useMemo } from 'react'
import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'
import { WalletIcon } from 'lucide-react'

import { Switch } from '@ems/ui/components/switch'
import { DataTable } from '@ems/ui/patterns/data-table'
import { EmptyState } from '@ems/ui/patterns/empty-state'
import { PageHeader } from '@ems/ui/patterns/page-header'
import { StatusBadge } from '@ems/ui/patterns/status-badge'

import { ReadOnlyNotice } from '#admin/components/read-only-notice.tsx'
import { sessionQuery } from '#admin/features/auth/queries.ts'
import { can } from '#admin/features/auth/session.ts'
import { PaymentMethodsSkeleton } from '#admin/features/billing/billing-skeletons.tsx'
import { useSetPaymentMethodEnabled } from '#admin/features/billing/mutations.ts'
import { KIND_LABEL, type AdminMethod } from '#admin/features/billing/payment-methods-api.ts'
import { paymentMethodsQuery } from '#admin/features/billing/queries.ts'

/**
 * `/billing/payment-methods` — the payment methods of this edition. Platform staff keep the
 * catalog, and here an operator turns each method on or off. When an operator submits a
 * registration, they select one of the methods that are on.
 */
export const Route = createFileRoute('/_authenticated/billing/payment-methods')({
  pendingComponent: PaymentMethodsSkeleton,
  staticData: { crumb: { parents: [{ label: 'Billing' }], label: 'Payment methods' } },
  loader: ({ context }) => context.queryClient.ensureQueryData(paymentMethodsQuery()),
  component: PaymentMethodsPage,
})

/** A method that a payer can select now: on, and not retired. */
const selectable = (method: AdminMethod) => method.enabled && !method.retired

/** What the operator must know about the switch, or null. */
function noteFor(method: AdminMethod): string | null {
  if (method.retired) return method.enabled ? 'Retired. Payers cannot select it.' : 'Retired'
  if (!method.enabled && method.provider !== null) return 'Needs online payment setup'
  return null
}

/**
 * The switch and its note. The note is the switch's description, so a screen reader reads why a
 * switch is locked. A component of its own for `useId`: the table and the phone cards both render
 * this cell, and an id from the method code would be on the page twice.
 */
function MethodSwitch({
  method,
  canManage,
  onChange,
}: {
  method: AdminMethod
  canManage: boolean
  onChange: (enabled: boolean) => void
}) {
  const noteId = useId()
  const note = noteFor(method)
  // A method that is off can be turned on only when it is not retired and needs no provider.
  const locked = !method.enabled && (method.retired || method.provider !== null)
  return (
    <span className="flex items-center justify-end gap-2">
      {note === null ? null : (
        <span id={noteId} className="text-muted-foreground text-xs">
          {note}
        </span>
      )}
      <Switch
        checked={method.enabled}
        disabled={!canManage || locked}
        aria-label={method.name}
        aria-describedby={note === null ? undefined : noteId}
        onCheckedChange={onChange}
      />
    </span>
  )
}

function PaymentMethodsPage() {
  const { data } = useSuspenseQuery(paymentMethodsQuery())
  const { data: session } = useSuspenseQuery(sessionQuery())
  const canManage = can(session, 'billing.manage')
  // Only `mutate`, which is stable. The columns depend on it, and new columns rebuild every cell:
  // the switch that the operator pressed would lose the keyboard focus. Each request sends the
  // value it sets, so a second press during the first request is safe.
  const { mutate: setEnabled } = useSetPaymentMethodEnabled()
  // Retired methods are left out of both numbers: payers cannot select them.
  const current = data.items.filter((method) => !method.retired)
  const onCount = current.filter(selectable).length

  const columns = useMemo<ColumnDef<AdminMethod, never>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Method',
        meta: { primary: true },
        cell: ({ row }) => (
          <span className="flex items-center gap-2">
            {row.original.name}
            {row.original.retired ? <StatusBadge tone="neutral">Retired</StatusBadge> : null}
          </span>
        ),
      },
      {
        id: 'type',
        header: 'Type',
        cell: ({ row }) => (
          <StatusBadge tone={row.original.provider === null ? 'neutral' : 'info'}>
            {row.original.provider === null ? 'Manual' : 'Online'}
          </StatusBadge>
        ),
      },
      {
        id: 'kind',
        header: 'Recorded as',
        meta: { hideOnCard: true },
        cell: ({ row }) => (
          <span className="text-muted-foreground">{KIND_LABEL[row.original.kind]}</span>
        ),
      },
      {
        id: 'enabled',
        header: 'On',
        meta: { align: 'end', shrink: true },
        cell: ({ row }) => (
          <MethodSwitch
            method={row.original}
            canManage={canManage}
            onChange={(enabled) => setEnabled({ code: row.original.code, enabled })}
          />
        ),
      },
    ],
    [canManage, setEnabled],
  )

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Payment methods"
        description="Turn on the methods that a payer can select when a registration is submitted. Invoices keep the method they were issued with."
      />

      {canManage ? null : <ReadOnlyNotice area="payment methods" />}

      <DataTable
        caption="Payment methods"
        title={`${onCount} of ${current.length} on`}
        columns={columns}
        data={data.items}
        getRowId={(method) => method.code}
        empty={
          <EmptyState
            icon={<WalletIcon />}
            title="No payment methods"
            description="Platform staff have not added a payment method yet."
          />
        }
      />
    </div>
  )
}
